import React, { useState, useEffect, useCallback } from 'react';
import { API, useAuth } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { UserPlus, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_OPTIONS = [
  { value: 'director', label: 'Director' },
  { value: 'admin', label: 'Admin' },
  { value: 'senior_executive', label: 'Senior Executive' },
  { value: 'executive', label: 'Executive' },
  { value: 'finance', label: 'Finance' },
];
const ADMIN_MANAGEABLE = ['senior_executive', 'executive'];
const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r.label]));
const ROLE_COLOR = {
  director: 'bg-violet-100 text-violet-700',
  admin: 'bg-amber-100 text-amber-700',
  senior_executive: 'bg-indigo-100 text-indigo-700',
  executive: 'bg-sky-100 text-sky-700',
  finance: 'bg-emerald-100 text-emerald-700',
};

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const isDirector = user?.role === 'director';
  const isAdmin = user?.role === 'admin';
  const canManage = isDirector || isAdmin;
  const roleOptions = isDirector ? ROLE_OPTIONS : ROLE_OPTIONS.filter((r) => ADMIN_MANAGEABLE.includes(r.value));
  const canManageMember = (member) =>
    canManage && member.id !== user.id && (isDirector || ADMIN_MANAGEABLE.includes(member.role));

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/staff/users`);
      setUsers(data);
    } catch (e) {
      toast.error('Failed to load team');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/staff/users/${id}`);
      toast.success('Member removed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleAssignManager = async (execId, seniorId) => {
    try {
      await axios.post(`${API}/staff/assign-executive`, {
        executive_id: execId,
        senior_executive_id: seniorId === 'none' ? null : seniorId,
      });
      toast.success('Team mapping updated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update mapping');
    }
  };

  const seniors = users.filter((u) => u.role === 'senior_executive');

  const subtitle = isDirector
    ? 'Manage admin roles and access'
    : isAdmin
      ? 'Add, edit or remove Senior Executives and Executives'
      : 'Executives in your team';

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="user-management-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Team</h1>
          <p className="text-lg text-slate-600">{subtitle}</p>
        </div>
        {canManage && <CreateUserDialog onDone={load} roleOptions={roleOptions} />}
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-indigo-600" />{users.length} member(s)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-slate-500 py-8">Loading...</p>
          ) : users.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No members yet.</p>
          ) : (
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 py-3 px-3 rounded-lg border border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <div>
                    <div className="font-semibold text-slate-900">{u.name}</div>
                    <div className="text-sm text-slate-500">{u.email}</div>
                    {u.whatsapp && (
                      <div className="text-xs text-emerald-700" data-testid={`user-whatsapp-${u.id}`}>WhatsApp: {u.whatsapp}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {['executive', 'senior_executive'].includes(u.role) && (
                      <span className="text-xs text-slate-500">{u.assigned_count || 0} students</span>
                    )}
                    {canManage && u.role === 'executive' && (
                      <Select value={u.managed_by || 'none'} onValueChange={(v) => handleAssignManager(u.id, v)}>
                        <SelectTrigger className="w-44 h-8 text-xs" data-testid={`reports-to-${u.id}`}>
                          <SelectValue placeholder="Reports to..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No manager</SelectItem>
                          {seniors.map((se) => <SelectItem key={se.id} value={se.id}>↳ {se.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    <Badge className={ROLE_COLOR[u.role]}>{ROLE_LABEL[u.role] || u.role}</Badge>
                    {canManageMember(u) && (
                      <div className="flex gap-1">
                        <EditUserDialog member={u} onDone={load} roleOptions={roleOptions} />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-rose-600" data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove {u.name}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This removes their access. Students assigned to them will become unassigned.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(u.id)} data-testid={`confirm-delete-${u.id}`}>Remove</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const CreateUserDialog = ({ onDone, roleOptions }) => {
  const [open, setOpen] = useState(false);
  const blank = { name: '', email: '', password: '', role: 'executive', whatsapp: '', designation: '' };
  const [form, setForm] = useState(blank);
  const submit = async () => {
    if (!form.name || !form.email || !form.password) return toast.error('Fill all fields');
    try {
      await axios.post(`${API}/staff/users`, form);
      toast.success('Member created');
      setOpen(false);
      setForm(blank);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-user-button"><UserPlus className="w-4 h-4 mr-2" />Add Member</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="create-user-name" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="create-user-email" /></div>
          <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="create-user-password" /></div>
          <div><Label>WhatsApp number</Label><Input placeholder="+91..." value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="create-user-whatsapp" /></div>
          <div><Label>Designation (optional)</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="create-user-designation" /></div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="create-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{roleOptions.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="create-user-submit">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditUserDialog = ({ member, onDone, roleOptions }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: member.name,
    email: member.email,
    role: member.role,
    whatsapp: member.whatsapp || '',
    designation: member.designation || '',
    password: '',
  });
  const submit = async () => {
    try {
      const payload = {
        name: form.name,
        email: form.email,
        role: form.role,
        whatsapp: form.whatsapp,
        designation: form.designation,
      };
      if (form.password) payload.password = form.password;
      await axios.put(`${API}/staff/users/${member.id}`, payload);
      toast.success('Member updated');
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };
  const options = roleOptions.some((r) => r.value === member.role)
    ? roleOptions
    : [...roleOptions, { value: member.role, label: ROLE_LABEL[member.role] || member.role }];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`edit-user-${member.id}`}>Edit</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit {member.name}</DialogTitle><DialogDescription>Update details, WhatsApp number, role or reset the password.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-user-name" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-user-email" /></div>
          <div><Label>WhatsApp number</Label><Input placeholder="+91..." value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="edit-user-whatsapp" /></div>
          <div><Label>Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="edit-user-designation" /></div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{options.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>New Password (optional)</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="edit-user-password" /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="edit-user-submit">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserManagement;
